import { streamText, stepCountIs, type LanguageModel, type StopCondition, type ToolSet } from 'ai';
import { type LLMMessage } from '@wtc/chat-types';
import type { AIConfig } from '../../config/config-types';
import {
  resolveLlmProvider,
  applyProviderCacheControl,
  type MeridianModelFactory,
} from '../providers';
import type { ToolExecutionContext } from '../tools';
import type { BedrockMessage, BedrockContentBlock, BedrockErrorLike } from './message-types';
import { sanitizeToBedrockMessages, stripOrphanToolUse } from './sanitize-bedrock-messages';
import {
  SINGLE_PASS_STEP_COUNT_LIMIT,
  SINGLE_PASS_STEP_COUNT_MAX,
  BEDROCK_MAX_OUTPUT_TOKENS,
  BEDROCK_MAX_RETRIES,
} from './constants';
import { buildStopConditions, resolveStepBudget, type StepBudget } from './step-budget';
import { getToolSearchConfig } from '../tools/tool-search';
import { setUpAppToolSearch, resolveActiveTools } from '../tools/tool-search/app-tool-search';
import {
  CODE_EXECUTION_TOOL_NAME,
  resolveCodeExecutionServerTool,
} from '../tools/server-tools/code-execution';
import { applyProgrammaticToolCalling } from '../tools/server-tools/programmatic-tool-calling';
import {
  buildToolsForAI,
  applyToolSearchDeferral,
  isDeferredTool,
  withCachedToolPrefix,
} from './tool-set-assembly';
import {
  createStreamErrorHandler,
  createStepFinishHandler,
  createFinishHandler,
  type StepLike,
  type FinishLike,
} from './stream-telemetry';
import { logger } from '../logger';

// ---------------------------------------------------------------------------
// This module was split by RESPONSIBILITY (rule
// MERIDIAN_SURFACE_PARITY_AND_MODULE_SIZE). It remains the ENTRY POINT: every
// symbol that moved is re-exported below, so no import path anywhere changes.
//
//   ./conversation-shape   — provider message-array rules (order/first-user/merge)
//   ./tool-set-assembly    — tool map: deferral + hot set + cached prefix
//   ./stream-telemetry     — turn observability, cost rows, stream-error routing
//
// What stays here is the ORCHESTRATION: resolve the provider, sanitize, choose a
// tool-assembly branch, attach server tools, and hand the config to `streamText`.
// ---------------------------------------------------------------------------
export {
  isValidBedrockConversation,
  ensureConversationStartsWithUser,
  mergeConsecutiveUserMessages,
  type BedrockConversationMessages,
} from './conversation-shape';
export {
  buildToolsForAI,
  applyToolSearchDeferral,
  isDeferredTool,
  withCachedToolPrefix,
} from './tool-set-assembly';
export {
  createStreamErrorHandler,
  createStepFinishHandler,
  createFinishHandler,
  type StepLike,
  type UsageLike,
  type FinishLike,
  type TurnLogContext,
  type TurnTelemetryContext,
} from './stream-telemetry';

/**
 * Create the Meridian model provider FACTORY for the globally-selected backend.
 *
 * This is a thin, backward-compatible wrapper over the provider registry
 * (`resolveLlmProvider`): it returns the callable provider factory so existing single-shot
 * callers can build their own model with a bespoke id — e.g. `provider(TOOL_SUMMARY_MODEL_ID)`.
 * Which backend it returns (Claude Platform on AWS by default, or DeepSeek when
 * `MERIDIAN_LLM_PROVIDER=deepseek`) is decided in one place — see `../providers`.
 *
 * AWS default: Anthropic's Messages API hosted inside our AWS account (explicit SigV4
 * credentials + `ANTHROPIC_AWS_WORKSPACE_ID`), NOT Amazon Bedrock's Converse API — it is what
 * supports tool search (`defer_loading`) and prompt caching. Model IDs are BARE first-party
 * ids (`claude-sonnet-4-6`) — no `anthropic.` Bedrock prefix.
 */
export async function createMeridianProvider(config: AIConfig): Promise<MeridianModelFactory> {
  const { provider } = await resolveLlmProvider(config);
  return provider;
}

/**
 * Stream response from Bedrock model.
 * Messages are always sanitized here (no empty content, no blank text) so Bedrock Converse API never rejects.
 *
 * @param messages - LLMMessage[] or model messages from convertToModelMessages; may have empty content or blank text
 * @param options.prepareStep - Optional callback to manage context window and modify messages before sending to Bedrock
 */
export async function streamBedrockResponse(
  config: AIConfig,
  messages: LLMMessage[] | readonly { role: string; content: unknown }[],
  systemPrompt: string,
  options?: {
    enableTools?: boolean;
    toolContext?: ToolExecutionContext;
    tools?: Record<string, unknown>;
    useToolSearch?: boolean; // Enable tool search for deferred tool loading
    /**
     * Per-turn agentic budget (steps + wall clock). Omit for the env/default
     * budget, which is sized for the serverless request timeout. Background
     * (queue-driven) turns are not bound by that timeout and pass a larger
     * `deadlineMs` explicitly. See `./step-budget`.
     */
    stepBudget?: Partial<StepBudget>;
    /**
     * Called when the loop stopped because it RAN OUT of budget while the model
     * still wanted to act — the step cap or the wall-clock deadline fired, not a
     * natural finish. This is the signal a caller needs to hand the remainder of
     * the work to a background turn instead of leaving the user to type
     * "continue". Never called on a normal completion.
     */
    onBudgetExhausted?: (info: {
      reason: 'step-cap' | 'deadline';
      stepCount: number;
      maxSteps: number;
    }) => void;
    /** AI SDK may pass stepNumber; messages/step can be optional. */
    prepareStep?: (params: {
      messages?: readonly { role: string; content: unknown }[];
      step?: number;
      stepNumber?: number;
    }) => Promise<{
      // Optional: the callback returns ONLY `system` for the common (in-window)
      // case so the SDK keeps ownership of the step message history (which holds
      // the provider-executed tool_search results the agentic loop needs).
      messages?: readonly { role: string; content: unknown }[];
      system?: string;
      // Per-step tool allow-list. Used by the app-level search-and-activate flow
      // (non-AWS providers) to widen the active set as tools are discovered.
      activeTools?: string[];
    }>;
  }
): Promise<any> {
  try {
    // ONE provider seam for the whole assistant. Default AWS-Claude; DeepSeek when
    // MERIDIAN_LLM_PROVIDER=deepseek (see ../providers). `providerKind` drives the
    // caching-parity + tool-search decisions below.
    const resolved = await resolveLlmProvider(config);
    const { model, providerKind } = resolved;

    // Tool search (deferLoading + the BM25 server tool) is a Claude-on-AWS Messages-API
    // feature exposed via provider.tools; DeepSeek has no equivalent, so it is unavailable
    // there and all tools load eagerly. Resolve the server tool once, narrowed by kind.
    const toolSearchServerTool =
      resolved.providerKind === 'aws' ? resolved.provider.tools.toolSearchBm25_20251119() : null;

    // Server-side code execution (Anthropic-operated sandbox). Same provider-tool
    // mechanism as tool search: AWS-Claude only, no client executor, resolves to
    // null when the MERIDIAN_CODE_EXECUTION flag is off. Appended to the tool map
    // AFTER the cached prefix below (its cache_control would be ignored anyway).
    const codeExecutionServerTool = resolveCodeExecutionServerTool(resolved, config.modelId, {
      chatId: options?.toolContext?.chatId,
      userId: options?.toolContext?.userId,
      modelId: config.modelId,
    });

    // Single place that guarantees Bedrock-safe messages (no empty content, no blank text in blocks)
    let bedrockMessages: BedrockMessage[] = sanitizeToBedrockMessages(messages);
    // Strip tool_use from assistant messages when the next message is not role 'tool' (avoids "tool_use ids without tool_result" after context-window slice)
    bedrockMessages = stripOrphanToolUse(bedrockMessages);

    // Provider-appropriate prompt caching (cache reads are EXCLUDED from the ITPM rate
    // limit — the main defense against the 10k-ITPM 429s). See MERIDIAN_PROMPT_CACHING.
    // For AWS this attaches explicit ephemeral breakpoints on the first (stable) user
    // message + the rolling last message; for DeepSeek it is a no-op (auto prefix cache).
    // Either way the stable-prefix/volatile-tail message ordering is unchanged, so both
    // providers' prefix caches keep hitting.
    applyProviderCacheControl({ messages: bedrockMessages }, providerKind);


    logger.debug('messages after sanitization', {
      op: 'streamBedrockResponse',
      messageCount: bedrockMessages.length,
      messages: bedrockMessages.map((m, idx) => ({
        index: idx,
        role: m.role,
        contentType: Array.isArray(m.content) ? 'array' : typeof m.content,
        content: Array.isArray(m.content) ? m.content : (typeof m.content === 'string' ? m.content.substring(0, 200) : m.content),
      })),
    });

    // Minimal structural views of the AI SDK step/finish payloads — only the
    // fields we log. Avoids `any` while sidestepping the SDK's heavy generics.
    // Correlate every loop log line to the invoking chat/user.
    const logCtx = {
      op: 'streamBedrockResponse',
      chatId: options?.toolContext?.chatId,
      userId: options?.toolContext?.userId,
    };

    // Fixed-prefix visibility. The system prompt + eager tool schemas are the
    // dominant, CONSTANT cost re-sent (or cache-read) on every step; user
    // messages are tiny by comparison. Logging their size once per turn makes
    // the ~20k-token baseline measurable so shrinking it is data-driven, not a
    // guess. ~4 chars/token is a coarse but stable estimate for English text.
    {
      const toolEntries = options?.tools ? Object.entries(options.tools) : [];
      const eagerToolCount = toolEntries.filter(([, t]) => !isDeferredTool(t)).length;
      logger.info('meridian request prefix size', {
        ...logCtx,
        op: 'streamBedrockResponse.prefixSize',
        systemPromptChars: systemPrompt.length,
        systemPromptEstTokens: Math.ceil(systemPrompt.length / 4),
        toolCountTotal: toolEntries.length,
        eagerToolCount,
        deferredToolCount: toolEntries.length - eagerToolCount,
        toolSearchEnabled: options?.useToolSearch !== false,
        messageCount: bedrockMessages.length,
      });
    }

    // Per-turn agentic budget, resolved ONCE here. `buildStopConditions` starts the
    // deadline clock, so it is called inline in the config below — immediately
    // before the loop starts, not during the setup above it.
    const stepBudget = resolveStepBudget(options?.stepBudget);

    /** Config for streamText; AI SDK accepts BedrockMessage[] via adapter. */
    const streamConfig: {
      model: LanguageModel;
      system: string;
      messages: BedrockMessage[];
      temperature?: number;
      maxOutputTokens?: number;
      maxRetries?: number;
      stopWhen: Array<StopCondition<ToolSet>>;
      prepareStep?: (params: { messages?: readonly { role: string; content: unknown }[]; step?: number; stepNumber?: number }) => Promise<{ messages?: readonly { role: string; content: unknown }[]; system?: string; activeTools?: string[] }>;
      tools?: Record<string, unknown>;
      /** Initial per-step tool allow-list (app-level tool search, non-AWS). */
      activeTools?: string[];
      onError?: (event: { error: unknown }) => void;
      onStepFinish?: (step: StepLike) => void;
      onFinish?: (event: FinishLike) => void;
    } = {
      model,
      system: systemPrompt,
      messages: bedrockMessages,
      temperature: config.temperature,
      // Cap output reservation. Default (model max, ~64k) bloats the request and
      // adds output-TPM pressure with no upside for a chat turn. See BEDROCK_MAX_OUTPUT_TOKENS.
      maxOutputTokens: BEDROCK_MAX_OUTPUT_TOKENS,
      // Retry discipline: the AI SDK default (2 retries) fires within seconds and
      // IGNORES the 429 `retry-after` header, burning the org's tiny req/min quota
      // and amplifying a rate-limit into a self-DoS. Keep retries low; let the user
      // re-send rather than hammer the limit. See BEDROCK_MAX_RETRIES.
      maxRetries: BEDROCK_MAX_RETRIES,
      // Bounded by whichever comes first: the step cap or the wall-clock deadline.
      // The deadline exists because raising the step cap makes it possible for a
      // turn to outlive the serverless function; a platform timeout kills the
      // stream mid-flight and `onFinish` never persists the turn. See ./step-budget.
      stopWhen: buildStopConditions(stepBudget),
      // Prompt caching (Claude Platform on AWS) is applied as PER-PART cache
      // breakpoints, NOT here: a top-level cacheControl is ignored by the AI SDK
      // (vercel/ai#5883). See withCachedToolPrefix (tools prefix, ./tool-set-assembly) and
      // applyProviderCacheControl (tools+system+first-message prefix, in ../providers),
      // and the MERIDIAN_PROMPT_CACHING rule. Cache reads are ITPM-exempt.
      // Turn observability, cost telemetry and stream-error routing all live
      // in ./stream-telemetry — see that module's header for why.
      onError: createStreamErrorHandler(logCtx),
      onStepFinish: createStepFinishHandler(logCtx),
      onFinish: createFinishHandler({
        logCtx,
        modelId: config.modelId,
        // The backend that actually served this turn, so the cost ledger stops
        // filing every row — DeepSeek included — under 'bedrock'.
        providerKind,
        stepBudget,
        chatId: options?.toolContext?.chatId,
        userId: options?.toolContext?.userId,
        onBudgetExhausted: options?.onBudgetExhausted,
      }),
    };

    if (options?.prepareStep) {
      streamConfig.prepareStep = options.prepareStep;
    }

    if (options?.enableTools) {
      if (options.tools) {
        // Caller supplied its own tool set (e.g. Hub `/chat`'s full registry).
        // Tool search is the standing DEFAULT (on unless explicitly false): defer
        // the cold tools out of the prompt and add the BM25 tool-search server
        // tool so Claude rediscovers them on demand — otherwise every caller tool
        // schema ships eagerly on every request. Available on AWS-Claude only
        // (`toolSearchServerTool` is null on DeepSeek → fixed eager set).
        if (toolSearchServerTool && options.useToolSearch !== false) {
          const searchConfig = getToolSearchConfig(true);
          const deferredTools = withCachedToolPrefix(
            applyToolSearchDeferral(options.tools, searchConfig)
          );
          // tool_search (provider tool) goes last; its cache_control is ignored.
          deferredTools.tool_search = toolSearchServerTool;
          streamConfig.tools = deferredTools;
        } else if (providerKind !== 'aws') {
          // No native tool search on this provider (DeepSeek). Run the app-level
          // search-and-activate flow: expose the full set but start with a small
          // `activeTools` allow-list (hot base + `search_tools`) and widen it as the
          // model discovers tools, so the per-step schema payload stays small. See
          // ../tools/tool-search/app-tool-search.
          const appSearch = setUpAppToolSearch(options.tools);
          streamConfig.tools = appSearch.tools;
          streamConfig.activeTools = appSearch.baseActiveTools;
          streamConfig.system = `${streamConfig.system}${appSearch.systemAddendum}`;
          // Compose (do not overwrite) the caller's prepareStep: keep its
          // context-window/system handling, add the growing active-tool set.
          const callerPrepareStep = streamConfig.prepareStep;
          streamConfig.prepareStep = async (params) => {
            const inner = callerPrepareStep ? await callerPrepareStep(params) : {};
            return { ...inner, activeTools: resolveActiveTools(appSearch) };
          };
        } else {
          // Tool search off explicitly on AWS-Claude — use the fixed set as-is.
          streamConfig.tools = options.tools;
        }
      } else {
        // Tool search ON by default (AWS-Claude only): defer cold tool schemas out of the
        // prompt and let Claude discover them via the server-side BM25 tool-search tool.
        const useToolSearch = !!toolSearchServerTool && (options.useToolSearch ?? true);
        const builtTools = withCachedToolPrefix(
          buildToolsForAI(options.toolContext, useToolSearch)
        );
        if (useToolSearch && toolSearchServerTool) {
          builtTools.tool_search = toolSearchServerTool;
        }
        streamConfig.tools = builtTools;
      }
    }

    // Attach the code-execution server tool once, AFTER every tool-assembly
    // branch above, so all three paths (caller-supplied set, app-level search,
    // built-in set) get it without three copies of this logic. Guarded on tools
    // existing at all: with `enableTools` false there is no tool map to join,
    // and a lone server tool is not a useful request.
    //
    // Order matters for prompt caching: provider-defined tools go LAST, after
    // the ephemeral breakpoint that `withCachedToolPrefix` places on the final
    // eager function tool. Appending here preserves the stable cached prefix
    // (see MERIDIAN_PROMPT_CACHING) — a provider tool's own cache_control is
    // ignored, so it must not sit inside the cached region.
    if (codeExecutionServerTool && streamConfig.tools) {
      streamConfig.tools[CODE_EXECUTION_TOOL_NAME] = codeExecutionServerTool;
    }

    // Programmatic tool calling: mark the eligible READ tools callable from
    // inside the code-execution container, so a paginated walk or a fat search
    // can be filtered there and only the answer enters the prompt.
    //
    // Applied AFTER the code-execution attach because it is a no-op without it
    // (`allowed_callers` naming an absent tool is a malformed request), and it
    // deliberately re-wraps entries already in the map rather than reordering
    // them — key order is unchanged, so the cached tool prefix and the
    // breakpoint `withCachedToolPrefix` placed above both survive.
    //
    // NOTE: adding `allowedCallers` changes those tool DEFINITIONS, which
    // invalidates the cached tool prefix once, on the deploy that flips the
    // flag. Expect one cold turn per conversation, then steady state.
    if (streamConfig.tools) {
      streamConfig.tools = applyProgrammaticToolCalling(streamConfig.tools, {
        codeExecutionAttached: !!codeExecutionServerTool,
        modelId: config.modelId,
        logContext: logCtx,
      });
    }

    const toolNames = streamConfig.tools ? Object.keys(streamConfig.tools) : [];
    const contentPreview = (c: BedrockMessage['content']) =>
      typeof c === 'string' ? c.substring(0, 100) : Array.isArray(c) ? `[${c.length} parts]` : '';

    logger.debug('calling streamText with config', {
      op: 'streamBedrockResponse',
      hasModel: !!streamConfig.model,
      messageCount: bedrockMessages.length,
      systemPromptLength: streamConfig.system.length,
      hasTools: !!streamConfig.tools,
      toolCount: toolNames.length,
      toolNames,
      messages: bedrockMessages.map((m) => ({
        role: m.role,
        contentType: Array.isArray(m.content) ? 'array' : typeof m.content,
        contentLength: Array.isArray(m.content) ? m.content.length : (typeof m.content === 'string' ? m.content.length : 0),
        contentPreview: contentPreview(m.content),
        contentStructure: Array.isArray(m.content) ? m.content.map((c: BedrockContentBlock) => ({
          type: c.type,
          hasText: 'text' in c,
          textLength: c.text ? String(c.text).length : 0,
        })) : 'string',
      })),
    });

    const result = await streamText(streamConfig as Parameters<typeof streamText>[0]);

    // Do NOT read result.text/.steps/.finishReason/.response/.usage here. Those are
    // lazy PROMISE getters that resolve only after the stream is consumed downstream
    // (see the onStepFinish/onFinish note above). Accessing them here floats an
    // uncaught promise per property; when the stream errors (e.g. an Anthropic 400
    // for a bad tool_use/tool_result pair) each rejects with AI_NoOutputGeneratedError
    // and Node reports an unhandledRejection — the "No output generated" noise we saw.
    // Final status is logged from resolved values in onStepFinish/onFinish instead.
    logger.debug('streamText returned', {
      op: 'streamBedrockResponse',
      resultKeys: Object.keys(result),
    });

    return result;
  } catch (error) {
    const err = error as Error & BedrockErrorLike;
    const isValidation = err.name === 'ValidationException' || String(err.message).includes('ValidationException');
    logger.error('error calling streamText', error, {
      op: 'streamBedrockResponse',
      isValidationException: isValidation,
      $metadata: err.$metadata,
      statusCode: err.statusCode,
      requestId: err.requestId,
    });
    throw error;
  }
}

/**
 * Generate text response from Bedrock (non-streaming).
 * Messages are sanitized for Bedrock (no empty content, no blank text).
 */
export async function generateBedrockResponse(
  config: AIConfig,
  messages: LLMMessage[] | readonly { role: string; content: unknown }[],
  systemPrompt: string,
  options?: {
    enableTools?: boolean;
    toolContext?: ToolExecutionContext;
    tools?: Record<string, any>; // Pre-built tools from hub (takes precedence over buildToolsForAI)
    useToolSearch?: boolean; // Enable tool search for deferred tool loading
    /**
     * Tool-call steps allowed before the loop stops. Only meaningful with
     * `enableTools`; defaults to `SINGLE_PASS_STEP_COUNT_LIMIT`.
     */
    maxSteps?: number;
  }
): Promise<string> {
  // Same provider seam as the streaming path (AWS-Claude default, or DeepSeek when toggled).
  const { model, providerKind } = await resolveLlmProvider(config);

  const bedrockMessages = sanitizeToBedrockMessages(messages);
  // Same provider-appropriate cache treatment as the streaming path so repeated single-shot
  // calls (quick-ask, summaries) reuse the tools+system+message prefix instead of re-paying
  // full input. AWS attaches ephemeral breakpoints; DeepSeek relies on its auto prefix cache
  // (no-op here). Cache reads are ITPM-exempt. See MERIDIAN_PROMPT_CACHING.
  applyProviderCacheControl({ messages: bedrockMessages }, providerKind);
  const streamConfig: any = {
    model,
    system: systemPrompt,
    messages: bedrockMessages,
    temperature: config.temperature,
  };

  // Add tools if enabled. This is the single-pass (non-streaming) path: there is no
  // tool-search server tool here to rediscover deferred tools, so never defer — load the
  // full set eagerly (callers like quick-ask pass their own small pre-built tool set).
  if (options?.enableTools) {
    // Eager set (no deferral on this single-pass path); cache the whole prefix.
    streamConfig.tools = withCachedToolPrefix(
      options.tools || buildToolsForAI(options.toolContext, false)
    );
    // WITHOUT THIS THE ANSWER IS EMPTY WHENEVER A TOOL IS USED. `streamText`
    // stops after ONE step by default: the model emits a tool call, the tool
    // runs, and the run ends there — with the tool result in `steps` and nothing
    // in `result.text`. Callers here ("what does Hub know about X?") ask
    // precisely the questions that need a lookup first, so the tool-using case is
    // the common case, not the edge. `stopWhen` lets the model come back with the
    // answer it went to fetch.
    streamConfig.stopWhen = stepCountIs(
      normalizeStepLimit(options.maxSteps) ?? SINGLE_PASS_STEP_COUNT_LIMIT
    );
  }

  const result = await streamText(streamConfig);

  return result.text;
}

/**
 * Clamp a caller-supplied step budget into a sane window, or ignore it.
 *
 * A non-finite or non-positive value is treated as "unset" rather than trusted:
 * this is a cost lever, and the failure mode of trusting it is a runaway loop.
 */
function normalizeStepLimit(value: number | undefined): number | undefined {
  if (value === undefined) return undefined;
  if (!Number.isFinite(value) || value < 1) return undefined;
  return Math.min(Math.floor(value), SINGLE_PASS_STEP_COUNT_MAX);
}
